/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/require-default-props */
/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import { useMonitorClipProduction } from '@src/monitoring/hooks';
import {
  useStore,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { createSpeakerFilteredTranscript } from '@src/utility/speakerUtils';
import { memo, useRef, useState } from 'react';

import { AIClipProcessor } from '../../services/AIClipProcessor';
import { produceClipsChunked } from '../../services/AIClipProducer';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { useAIClipProducerTheme } from './useAIClipProducerTheme';

import type { TranscriptElement } from '../../types.ts/transcript';
import type { AudienceOption } from '../common/AudienceSelector';
import type { GoalOption } from '../common/GoalSelector';
import type { Speaker } from '../content-components/SpeakerTypes';
import type { FunctionComponent } from 'react';

type ClipProducerProps = {
  ClipProducerUI: FunctionComponent<ClipProducerUIProps>;
  useFinalOrOriginalVideo?: boolean;
  supportedPresets: { title: string; category: string }[] | null;
  enableSpeakerSelection?: boolean; // Added to control speaker selection feature
};

export type ClipProducerUIProps = {
  supportedPresets: { title: string; category: string }[];
  isProducing: boolean;
  retryCount: number;
  selectedDuration: number | null;
  setSelectedDuration: (duration: number | null) => void;
  selectedPresets: string[];
  setSelectedPresets: (presets: string[]) => void;
  onPresetClick: (preset: string) => void;
  customPresetText: string;
  setCustomPresetText: (text: string) => void;
  selectedSocials: string[];
  setSelectedSocials: (socials: string[]) => void;
  producedClips: any[] | null;
  showModal: boolean;
  isProducingDisable: boolean;
  onProduceButtonClick: () => void;
  closeProducingModal: () => void;
  // Suggested topics (clip themes) props
  suggestedThemes: string[];
  selectedSuggestedTheme: string | null;
  onSuggestedThemeClick: (title: string) => void;
  // New speaker selection props
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  setSelectedSpeakers: (speakers: Speaker[]) => void;
  useSpeakerFilter: boolean;
  setUseSpeakerFilter: (useFilter: boolean) => void;
  isLoadingSpeakers: boolean;
  showSpeakerSelector: boolean;
  // Goal selection props
  selectedGoal: GoalOption | null;
  setSelectedGoal: (goal: GoalOption | null) => void;
  useGoalFilter: boolean;
  setUseGoalFilter: (useFilter: boolean) => void;
  // Audience selection props
  selectedAudiences: AudienceOption[];
  setSelectedAudiences: (audiences: AudienceOption[]) => void;
  useAudienceFilter: boolean;
  setUseAudienceFilter: (useFilter: boolean) => void;
};

const AIClipProducer = memo(
  (props: ClipProducerProps) => {
    const videoCreator = useVideoCreatorStore();
    const rootStore = useStore();
    const transcriptionStore = useTranscription();
    const {
      ClipProducerUI,
      useFinalOrOriginalVideo = false,
      enableSpeakerSelection = false,
    } = props;
    const userIdentity = useUserIdentity();
    const { enableParallelClipSave } = useFlagsCombination(
      userIdentity.currentRole,
    );
    const {
      selectedPresets,
      setSelectedPresets,
      onPresetClick,
      suggestedThemes,
      selectedSuggestedTheme,
      onSuggestedThemeClick,
      speakers,
      selectedSpeakers,
      setSelectedSpeakers,
      useSpeakerFilter,
      setUseSpeakerFilter,
      isLoadingSpeakers,
      showSpeakerSelector,
    } = useAIClipProducerTheme({ enableSpeakerSelection });
    const [selectedSocials, setSelectedSocials] = useState<string[]>([]);
    const [selectedDuration, setSelectedDuration] = useState<number | null>(
      null,
    );
    const [isProducing, setIsProducing] = useState(false);
    const [producedClips, setProducedClips] = useState<any[] | null>(null);
    const producerAbortController = useRef<{
      id: string;
      aborted: boolean;
    } | null>(null);
    const [showModal, setShowModal] = useState(false);

    const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(null);
    const [useGoalFilter, setUseGoalFilter] = useState(false);
    const [selectedAudiences, setSelectedAudiences] = useState<
      AudienceOption[]
    >([]);
    const [useAudienceFilter, setUseAudienceFilter] = useState(false);

    const MAX_RETRIES = 3;

    const [presetInputValue, setPresetInputValue] = useState<string>('');

    const [retryCount, setRetryCount] = useState<number>(0);
    const retryCountRef = useRef(retryCount);
    retryCountRef.current = retryCount;

    // Track completion to prevent multiple completion calls
    const hasCompletedRef = useRef(false);
    const monitoring = useMonitorClipProduction({
      enabled: true,
      includeDetailedMetadata: true,
      trackPerformance: true,
    });

    const closeProducingModal = () => {
      if (producerAbortController.current && isProducing) {
        producerAbortController.current.aborted = true;
        monitoring.cancelProduction('User cancelled clip production');
      }
      setProducedClips(null);
      setIsProducing(false);
      setShowModal(false);
    };

    const isProducingDisabled = () =>
      !(selectedPresets.length || presetInputValue || selectedSuggestedTheme) ||
      isProducing ||
      !videoCreator.story?.originalVideo ||
      // Add condition to check if speaker filter is active but no speakers selected
      (useSpeakerFilter && selectedSpeakers.length === 0);

    const onProduceButtonClick = async () => {
      if (
        !(selectedPresets.length || presetInputValue || selectedSuggestedTheme)
      ) {
        return;
      }

      // Start clip production tracking
      const trackingMetadata = {
        theme: selectedPresets[0] || 'Custom',
        customTheme: selectedSuggestedTheme || presetInputValue || undefined,
        platform: selectedSocials[0] || undefined,
        duration: selectedDuration,
        useSpeakerFilter,
        selectedSpeakersCount: selectedSpeakers.length,
        useGoalFilter,
        selectedGoal: selectedGoal?.id,
        useAudienceFilter,
        selectedAudiencesCount: selectedAudiences.length,
        storyId: videoCreator.story?.id,
        videoId: videoCreator.currentVideo?.id,
      };

      // Reset completion tracking for new production
      hasCompletedRef.current = false;
      monitoring.startProduction(trackingMetadata);

      setIsProducing(true);
      setShowModal(true);
      producerAbortController.current = {
        id: Date.now().toString(),
        aborted: false,
      };

      const finalVideo = videoCreator.story?.finalVideo;

      if (
        useFinalOrOriginalVideo &&
        finalVideo?.id &&
        finalVideo.videoFilePrimary?.video
      ) {
        await videoCreator.loadVideoWithoutRendering(finalVideo.id, true);
        videoCreator.duration = finalVideo.videoFilePrimary!.video.duration;
      } else if (useFinalOrOriginalVideo) {
        await videoCreator.createNewVideoFromSource();
        videoCreator.duration = parseFloat(videoCreator.originalVideoDuration);
      }

      // Get original transcript elements
      const originalElements = videoCreator.currentVideo?.videoFilePrimary
        ? transcriptionStore.getFinalTranscriptionElements()
        : transcriptionStore.originalTranscription?.elements || [];

      // Apply speaker filtering if enabled
      let transcriptElements = originalElements;
      const transcriptionText =
        videoCreator.videoTranscriptionProcessor.getFinalTranscriptionText();
      let submitTranscriptionText = transcriptionText;

      if (useSpeakerFilter && selectedSpeakers.length > 0) {
        // Use our utility function to create a filtered transcript with speaker attribution
        const filteredTranscript = createSpeakerFilteredTranscript(
          originalElements,
          selectedSpeakers,
        );
        submitTranscriptionText = filteredTranscript.text;
      }

      if (enableParallelClipSave && videoCreator.currentVideo) {
        // This is needed so that the produced clips' transcriptPosition is
        // predictable when saving clips in parallel
        try {
          const editor = AIClipProcessor.newVideoEditor(
            videoCreator.currentVideo,
            videoCreator,
          );
          if (editor.getFullTranscript().length > 0) {
            transcriptElements =
              editor.getFullTranscript() as TranscriptElement[];
          }
        } catch (e) {
          // AIClipProcessor will log errors
        }
      }

      while (retryCountRef.current < MAX_RETRIES) {
        try {
          const matchedParts = await produceClipsChunked({
            videoCreator,
            originalVideo:
              videoCreator.currentVideo?.videoFilePrimary ||
              videoCreator.story!.originalVideo!,
            submitTranscriptionText,
            transcriptionText,
            transcriptionElements: transcriptElements,
            originalDuration: videoCreator.duration,
            duration: selectedDuration,
            theme: selectedPresets[0] || 'Custom',
            customTheme:
              selectedSuggestedTheme || presetInputValue || undefined,
            themeDisplayName: selectedSuggestedTheme || undefined,
            platform: selectedSocials[0] || undefined,
            onClipsProduced: (clips: any) => {
              setProducedClips(prevClips =>
                clips.length !== prevClips?.length ? [...clips] : prevClips,
              );

              // Complete workflow when first clips are produced and shown
              if (
                clips.length > 0 &&
                monitoring.isEnabled &&
                !hasCompletedRef.current
              ) {
                hasCompletedRef.current = true; // Prevent multiple completions
                monitoring.completeProduction(
                  clips.length,
                  retryCountRef.current,
                  {
                    finalTheme: selectedPresets[0] || 'Custom',
                    finalPlatform: selectedSocials[0] || undefined,
                  },
                );
              }
            },
            abortToken: producerAbortController.current,
            rootStore,
            speakers: useSpeakerFilter ? selectedSpeakers : [], // Pass selected speakers for name lookup
            goal: useGoalFilter ? selectedGoal : null, // Pass selected goal for context
            audiences: useAudienceFilter ? selectedAudiences : [], // Pass selected audiences
          });
          console.log('ALL PRODUCED CLIPS', matchedParts);
          if (!matchedParts?.length) {
            setRetryCount(prev => prev + 1);
          } else {
            setProducedClips(matchedParts);
            break;
          }
        } catch (err: any) {
          console.error(err);
          if (err.message === 'Aborted') {
            return;
          }
          setRetryCount(prev => prev + 1);
        }
      }

      // If we've exhausted all retries and still have no clips, fail the workflow
      if (retryCountRef.current >= MAX_RETRIES && monitoring.isProducing) {
        monitoring.failProduction(
          retryCountRef.current,
          MAX_RETRIES,
          'No clips produced after maximum retries',
          {
            theme: selectedPresets[0] || 'Custom',
            platform: selectedSocials[0] || undefined,
          },
        );
      }

      producerAbortController.current = null;
      setRetryCount(0);
      setIsProducing(false);
    };

    return (
      <ClipProducerUI
        supportedPresets={props.supportedPresets || []}
        isProducing={isProducing}
        retryCount={retryCount}
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
        selectedPresets={selectedPresets}
        setSelectedPresets={setSelectedPresets}
        onPresetClick={onPresetClick}
        customPresetText={presetInputValue}
        setCustomPresetText={setPresetInputValue}
        selectedSocials={selectedSocials}
        setSelectedSocials={setSelectedSocials}
        producedClips={producedClips}
        showModal={showModal}
        isProducingDisable={isProducingDisabled()}
        onProduceButtonClick={onProduceButtonClick}
        closeProducingModal={closeProducingModal}
        // Suggested topics (clip themes)
        suggestedThemes={suggestedThemes}
        selectedSuggestedTheme={selectedSuggestedTheme}
        onSuggestedThemeClick={onSuggestedThemeClick}
        // New speaker selection props
        speakers={speakers}
        selectedSpeakers={selectedSpeakers}
        setSelectedSpeakers={setSelectedSpeakers}
        useSpeakerFilter={useSpeakerFilter}
        setUseSpeakerFilter={setUseSpeakerFilter}
        isLoadingSpeakers={isLoadingSpeakers}
        showSpeakerSelector={showSpeakerSelector}
        // Goal selection props
        selectedGoal={selectedGoal}
        setSelectedGoal={setSelectedGoal}
        useGoalFilter={useGoalFilter}
        setUseGoalFilter={setUseGoalFilter}
        // Audience selection props
        selectedAudiences={selectedAudiences}
        setSelectedAudiences={setSelectedAudiences}
        useAudienceFilter={useAudienceFilter}
        setUseAudienceFilter={setUseAudienceFilter}
      />
    );
  },
  (oldProps, newProps) =>
    oldProps.supportedPresets?.length === newProps.supportedPresets?.length,
);

export default AIClipProducer;
